import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Link2 } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import EmpleadoPicker from './EmpleadoPicker';
import { fechaCorta, diasEntre, duracionLegible, parsePredecesoras, formatPredecesoras } from './utils';
import { FOCUS } from '../utils/uiTokens';

/**
 * Paso 2 del modal "Nuevo proyecto" cuando se parte de una plantilla: el
 * checklist de actividades, editable antes de crear. Cada fila se puede
 * activar/desactivar y ajustar (titulo, responsable, duracion). La tira de la
 * derecha dibuja cuando cae cada actividad segun las fechas que calcula el
 * backend (/previsualizar), asi se ve el largo del proyecto antes de crearlo.
 */
const PlantillaActividadesPaso = ({ plantillaId, actividades, setActividades, fechaInicio, empleados, color }) => {
  const [preview, setPreview] = useState(null);

  // Recalcula fechas con debounce cuando cambia algo que las afecta.
  const firma = useMemo(
    () => JSON.stringify(actividades.map((a) => [a.orden, a.incluir, a.duracion_dias, a.offset_dias, a.predecesoras])),
    [actividades]
  );
  useEffect(() => {
    const t = setTimeout(() => {
      axiosInstance
        .post('/api/proyecto-plantillas/previsualizar', {
          plantilla_id: plantillaId,
          fecha_inicio: fechaInicio,
          actividades: actividades.map((a) => ({
            orden: a.orden,
            titulo: a.titulo || '-',
            duracion_dias: a.duracion_dias,
            offset_dias: a.offset_dias,
            predecesoras: a.predecesoras,
            incluir: a.incluir,
          })),
        })
        .then(({ data }) => setPreview(data))
        .catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, fechaInicio, plantillaId]);

  const fechasPorOrden = useMemo(() => {
    const m = new Map();
    (preview?.actividades || []).forEach((a) => m.set(a.orden, a));
    return m;
  }, [preview]);

  // Dias inclusivos: del 1 al 3 son 3 dias (misma convencion que el backend).
  const total = preview ? Math.max(diasEntre(preview.fecha_inicio, preview.fecha_fin) + 1, 1) : 1;
  const activas = actividades.filter((a) => a.incluir).length;
  const todasActivas = activas === actividades.length;

  const actualizar = (orden, cambios) =>
    setActividades((prev) => prev.map((a) => (a.orden === orden ? { ...a, ...cambios } : a)));

  const toggleTodas = () =>
    setActividades((prev) => prev.map((a) => ({ ...a, incluir: !todasActivas })));

  const agregar = () =>
    setActividades((prev) => {
      const orden = prev.reduce((m, a) => Math.max(m, a.orden), 0) + 1;
      const ultima = prev.filter((a) => a.incluir).slice(-1)[0];
      return [
        ...prev,
        {
          orden,
          titulo: '',
          asignado_id: null,
          responsable_texto: null,
          duracion_dias: 5,
          offset_dias: null,
          prioridad: 'ninguna',
          predecesoras: ultima ? [ultima.orden] : [],
          hito: null,
          incluir: true,
          nueva: true,
        },
      ];
    });

  const quitar = (orden) =>
    setActividades((prev) =>
      prev
        .filter((a) => a.orden !== orden)
        .map((a) => ({ ...a, predecesoras: (a.predecesoras || []).filter((p) => p !== orden) }))
    );

  return (
    <div className="flex flex-col min-h-0">
      {/* Resumen */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-6 pb-3">
        <p className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900 tabular-nums">{activas}</span> de{' '}
          <span className="tabular-nums">{actividades.length}</span> actividades se crearán como tareas
          {preview && (
            <>
              {' '}y el proyecto termina el{' '}
              <span className="font-semibold text-slate-900">{fechaCorta(preview.fecha_fin)}</span>
              <span className="text-slate-500"> ({duracionLegible(total)})</span>
            </>
          )}
          .
        </p>
        <button
          type="button"
          onClick={toggleTodas}
          className="text-xs font-medium text-cyan-700 hover:text-cyan-900"
        >
          {todasActivas ? 'Desmarcar todas' : 'Marcar todas'}
        </button>
      </div>

      {/* Tabla */}
      <div className="border-y border-slate-200/80 overflow-auto max-h-[52vh]">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] text-slate-500">
            <tr>
              <th className="w-10 py-2 pl-6 pr-1 text-left font-medium" />
              <th className="w-8 py-2 px-1 text-right font-medium">N.º</th>
              <th className="py-2 px-2 text-left font-medium">Actividad</th>
              <th className="w-48 py-2 px-2 text-left font-medium">Responsable</th>
              <th className="w-20 py-2 px-2 text-right font-medium">Días</th>
              <th className="w-56 py-2 pl-3 pr-6 text-left font-medium hidden md:table-cell">
                {preview ? (
                  <span className="flex justify-between tabular-nums">
                    <span>{fechaCorta(preview.fecha_inicio)}</span>
                    <span>{fechaCorta(preview.fecha_fin)}</span>
                  </span>
                ) : (
                  'Calendario'
                )}
              </th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {actividades.map((a) => {
              const f = fechasPorOrden.get(a.orden);
              const ini = f ? diasEntre(preview.fecha_inicio, f.fecha_inicio) : 0;
              const dur = f && f.fecha_vencimiento ? Math.max(diasEntre(f.fecha_inicio, f.fecha_vencimiento) + 1, 0) : 0;
              return (
                <tr
                  key={a.orden}
                  className={`border-t border-slate-100 align-top transition-opacity ${a.incluir ? '' : 'opacity-45'}`}
                >
                  <td className="py-2 pl-6 pr-1">
                    <input
                      type="checkbox"
                      checked={a.incluir}
                      onChange={(e) => actualizar(a.orden, { incluir: e.target.checked })}
                      aria-label={`Incluir actividad ${a.orden}`}
                      className="mt-1 h-4 w-4 rounded border-slate-300 accent-cyan-600 cursor-pointer"
                    />
                  </td>
                  <td className="py-2 px-1 text-right text-xs text-slate-400 tabular-nums pt-2.5">{a.orden}</td>
                  <td className="py-1.5 px-2 min-w-[14rem]">
                    <input
                      value={a.titulo}
                      onChange={(e) => actualizar(a.orden, { titulo: e.target.value })}
                      placeholder="Nombre de la actividad"
                      maxLength={200}
                      disabled={!a.incluir}
                      className={`w-full px-1.5 py-1 text-sm text-slate-800 bg-transparent border border-transparent rounded-md hover:border-slate-200 ${FOCUS.input}`}
                    />
                    {a.offset_dias != null ? (
                      <p className="px-1.5 mt-0.5 text-[11px] text-slate-400">
                        Arranca el día {a.offset_dias} del proyecto{a.hito ? ` · hito ${a.hito}` : ''}
                      </p>
                    ) : (
                    <div className="flex items-center gap-1 px-1.5 mt-0.5 text-[11px] text-slate-400">
                      <Link2 size={10} className="flex-shrink-0" />
                      <span>Después de</span>
                      <input
                        value={a._predTexto ?? formatPredecesoras(a.predecesoras)}
                        onChange={(e) => actualizar(a.orden, { _predTexto: e.target.value })}
                        onBlur={(e) =>
                          actualizar(a.orden, {
                            _predTexto: undefined,
                            predecesoras: parsePredecesoras(e.target.value).filter((p) => p !== a.orden),
                          })
                        }
                        placeholder="inicio"
                        disabled={!a.incluir}
                        aria-label={`Predecesoras de la actividad ${a.orden}`}
                        className="w-20 px-1 py-0 text-[11px] text-slate-500 bg-transparent border-b border-transparent hover:border-slate-200 focus:outline-none focus:border-cyan-500 tabular-nums"
                      />
                      {a.hito && <span className="ml-1 truncate">· hito {a.hito}</span>}
                    </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2">
                    <EmpleadoPicker
                      value={a.asignado_id}
                      onChange={(id) => actualizar(a.orden, { asignado_id: id })}
                      empleados={empleados}
                      rolTexto={a.responsable_texto}
                      disabled={!a.incluir}
                    />
                  </td>
                  <td className="py-1.5 px-2">
                    <input
                      type="number"
                      min={0}
                      max={999}
                      value={a.duracion_dias ?? ''}
                      onChange={(e) =>
                        actualizar(a.orden, {
                          duracion_dias: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                      disabled={!a.incluir}
                      aria-label={`Duración en días de la actividad ${a.orden}`}
                      className={`w-full px-1.5 py-1 text-sm text-right tabular-nums text-slate-800 bg-transparent border border-transparent rounded-md hover:border-slate-200 ${FOCUS.input}`}
                    />
                  </td>
                  <td className="py-2 pl-3 pr-6 hidden md:table-cell">
                    <div
                      className="relative h-5 mt-1 rounded-sm bg-slate-100/70"
                      title={f ? `${fechaCorta(f.fecha_inicio)} al ${f.fecha_vencimiento ? fechaCorta(f.fecha_vencimiento) : 'sin fecha fin'}` : ''}
                    >
                      {a.incluir && f && (
                        <div
                          className="absolute top-1 bottom-1 rounded-sm"
                          style={{
                            left: `${(ini / total) * 100}%`,
                            width: dur ? `${Math.max((dur / total) * 100, 1.5)}%` : '3px',
                            backgroundColor: color,
                            opacity: dur ? 0.85 : 0.5,
                          }}
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    {a.nueva && (
                      <button
                        type="button"
                        onClick={() => quitar(a.orden)}
                        className="mt-0.5 p-1 text-slate-300 hover:text-rose-600 rounded-md"
                        aria-label="Quitar actividad"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-6 pt-2.5">
        <button
          type="button"
          onClick={agregar}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
        >
          <Plus size={13} /> Añadir actividad
        </button>
        <p className="mt-1 text-[11px] text-slate-400">
          Las actividades desmarcadas no se crean; las que dependían de ellas pasan a esperar a la actividad anterior.
          Los responsables se agregan como miembros del proyecto.
        </p>
      </div>
    </div>
  );
};

export default PlantillaActividadesPaso;
